import { Link } from '@inertiajs/react';
import Icon from '@/components/public/ui/Icon';
import type { ReactNode } from 'react';

interface ButtonProps {
  children: ReactNode;
  variant?: 'primary' | 'secondary' | 'ghost' | 'gold' | 'green' | 'white';
  size?: 'sm' | 'md' | 'lg';
  href?: string;
  onClick?: () => void;
  className?: string;
  type?: 'button' | 'submit';
  icon?: string;
  iconAfter?: string;
  disabled?: boolean;
  loading?: boolean;
}

// «Granito & Ouro» buttons: squared (rounded-md), solid fills or bordered
// surfaces. Hover is COLOUR-ONLY — no lift, no scale, no pop (institutional
// restraint). See design-system.md §5.1.
const variantClasses = {
  primary: 'bg-primary text-white hover:bg-primary-dark',
  secondary:
    'bg-surface border border-line-strong text-on-surface hover:border-primary hover:text-primary',
  ghost:
    'bg-transparent border border-line-strong text-on-surface hover:border-primary hover:text-primary',
  gold: 'bg-secondary text-white hover:bg-secondary-dark',
  green: 'bg-tertiary text-white hover:bg-tertiary-dark',
  // `white` is deprecated (it existed for dark backdrops, which are gone).
  // Aliased to the bordered-surface look so existing call-sites keep working.
  white:
    'bg-surface border border-line-strong text-on-surface hover:border-primary hover:text-primary',
};

const sizeClasses = {
  sm: 'text-sm px-3.5 py-2',
  md: 'text-base px-5 py-2.5',
  lg: 'text-lg px-7 py-3.5',
};

export default function Button({
  children,
  variant = 'primary',
  size = 'md',
  href,
  onClick,
  className = '',
  type = 'button',
  icon,
  iconAfter,
  disabled = false,
  loading = false,
}: ButtonProps) {
  const isDisabled = disabled || loading;
  const classes = `inline-flex items-center justify-center gap-2 rounded-md font-semibold transition-colors duration-200 ${variantClasses[variant]} ${sizeClasses[size]} ${isDisabled ? 'cursor-not-allowed opacity-60' : ''} ${className}`;

  const content = (
    <>
      {loading && (
        <span
          className="h-4 w-4 animate-spin rounded-full border-2 border-white/45 border-t-white"
          aria-hidden="true"
        />
      )}
      {!loading && icon && <Icon name={icon} size="sm" />}
      {children}
      {!loading && iconAfter && <Icon name={iconAfter} size="sm" />}
    </>
  );

  if (href) {
    return (
      <Link href={href} onClick={onClick} className={classes}>
        {content}
      </Link>
    );
  }

  return (
    <button type={type} onClick={onClick} disabled={isDisabled} className={classes}>
      {content}
    </button>
  );
}
